/**
 * PHOTO CARD COMPONENT
 * 
 * Single photo thumbnail with metadata
 */

import React from 'react';
import { Trash2, Edit2, Link } from 'lucide-react';
import { getPhotoCategory } from '../../constants/photoCategories';

const PhotoCard = ({ photo, areas = [], onEdit, onRemove, disabled = false }) => {
  const category = getPhotoCategory(photo.category);
  
  // Find linked area/fixture
  const linkedArea = areas.find(a => a.area_id === photo.area_id);
  const linkedFixtureLabel = photo.fixture_key && linkedArea?.fixtures?.[photo.fixture_key]
    ? photo.fixture_key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())
    : null;

  return (
    <div className="relative group border border-slate-700 rounded overflow-hidden bg-slate-900/50">
      {/* Image */}
      <div className="aspect-square bg-slate-800">
        {photo.file_url ? (
          <img 
            src={photo.file_url} 
            alt={photo.caption || 'Photo'} 
            className="w-full h-full object-cover"
          />
        ) : photo.thumbnail_url ? (
          <img 
            src={photo.thumbnail_url} 
            alt={photo.caption || 'Photo'} 
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-4xl text-slate-600">
            📷
          </div>
        )}
      </div>
      
      {/* Overlay on hover */}
      <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
        <button
          onClick={() => onEdit?.(photo.photo_id)}
          className="p-2 bg-slate-700 hover:bg-slate-600 rounded text-slate-200"
          title="Edit photo"
        >
          <Edit2 className="w-4 h-4" />
        </button>
        {!disabled && (
          <button
            onClick={() => onRemove?.(photo.photo_id)}
            className="p-2 bg-red-500/80 hover:bg-red-500 rounded text-white"
            title="Remove photo"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        )}
      </div>
      
      {/* Category Badge */}
      <div className="absolute top-2 left-2 px-2 py-0.5 bg-slate-900/80 rounded text-xs text-slate-300">
        {category.icon} {category.label}
      </div>
      
      {/* Linkage indicator */}
      {(linkedArea || linkedFixtureLabel) && (
        <div className="absolute top-2 right-2 p-1 bg-emerald-500/80 rounded">
          <Link className="w-3 h-3 text-white" />
        </div>
      )}
      
      {/* Caption */}
      <div className="p-2 border-t border-slate-700">
        <p className="text-xs text-slate-300 truncate">
          {photo.caption || 'No caption'}
        </p>
        {linkedArea && (
          <p className="text-xs text-slate-500 truncate mt-0.5">
            {linkedArea.label}
            {linkedFixtureLabel && ` → ${linkedFixtureLabel}`}
          </p>
        )}
      </div>
    </div>
  );
};

export default PhotoCard;
